import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Label, TextField } from "react-aria-components";
import { ChevronRight, Eye, Layers, Plus } from "lucide-react";
import type { PmMicroScenarioView, PmStep, PmStepAnnotation, PmVariant, PmVariantAnnotation } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Dialog, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { AuthSession } from "../../auth/keycloak.js";
import { formatDate, phaseLabel, phaseTone } from "./pm-labels.js";
import { PmMarkdown } from "./PmMarkdown.js";
import { PmNote, type PmNoteData } from "./PmNote.js";
import { StatusChip } from "./StatusChip.js";
import { PmNewTaskDialog } from "./PmNewTaskDialog.js";
import { usePmDrehbuchDiff, useStepAnnotationWrites, useVariantAnnotationWrites } from "./usePmBoard.js";

/**
 * The Drehbuch view (ADR-055 phase 5, mockup "2a"): the read-only script of the MS's current phase — the Überblick
 * (long, so it opens in its own panel, without notes), readiness, variants, the steps with their points — and, under every variant and step, that phase's annotations with a
 * box to add one (D2: every PM user writes; the author edits their own; the author or a PM admin deletes). "Neuer Task"
 * opens the overlay, pre-set to the step it was started from. Readiness counts every task on the board: done is what sits in
 * the done column, everything else is open, parked tasks included (owner 2026-11-02).
 */
export function PmDrehbuch({ session, meId, data, onBack }: { session: AuthSession; meId: number; data: PmMicroScenarioView; onBack: () => void }) {
  const { t, i18n } = useTranslation();
  const [newTaskStep, setNewTaskStep] = useState<number | null | undefined>(undefined); // undefined = closed
  const [showDiff, setShowDiff] = useState(false);
  const [showOverview, setShowOverview] = useState(false);
  const { ms } = data;
  const hasScript = ms.drehbuchVersion !== null;
  const diff = usePmDrehbuchDiff(session, ms.id, hasScript);
  const current = diff.data?.versions.find((v) => v.version === ms.drehbuchVersion);
  const doneCol = data.columns.find((c) => c.role === "done");
  const tasks = data.tasks;
  const done = tasks.filter((x) => x.columnId === doneCol?.id).length;
  const openByColumn = data.columns.filter((c) => c.role !== "done").map((c) => ({ ...c, count: tasks.filter((x) => x.columnId === c.id).length }));

  return (
    <div className="flex max-w-4xl flex-col gap-6">
      <header className="flex flex-col gap-3">
        <nav aria-label={t("pmBoard.overview")} className="text-muted-foreground text-xs">
          <Button variant="link" size="xs" onPress={onBack} className="h-auto px-0">
            {t("pmBoard.overview")}
          </Button>
        </nav>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="flex-1 text-2xl font-bold tracking-tight">
            {ms.code} – {ms.title}
          </h1>
          {hasScript ? (
            <Button variant="outline" onPress={() => setNewTaskStep(null)}>
              <Plus aria-hidden="true" />
              {t("pmBoard.newTask")}
            </Button>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusChip tone={phaseTone(ms.phase)}>{phaseLabel(t, ms)}</StatusChip>
          {hasScript && current ? (
            <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs">
              {t("pmBoard.drehbuchVersion", { version: ms.drehbuchVersion, date: formatDate(current.importedAt, i18n.language) })}
            </span>
          ) : null}
          {diff.data?.from != null ? (
            <Button variant="link" size="xs" aria-expanded={showDiff} aria-controls="pm-diff" onPress={() => setShowDiff((v) => !v)} className="h-auto px-0">
              {showDiff ? t("pmBoard.diff.hide") : t("pmBoard.diff.show", { version: diff.data.from })}
            </Button>
          ) : null}
        </div>
      </header>

      {showDiff && diff.data ? <DiffPanel diff={diff.data} /> : null}

      {!hasScript ? (
        <p className="bg-background text-muted-foreground rounded-xl p-5 text-sm shadow-sm">{t("pmBoard.noDrehbuch")}</p>
      ) : (
        <>
          {data.overview ? (
            <section aria-labelledby="pm-summary" className="bg-background flex items-center gap-3 rounded-xl px-5 py-4 shadow-sm">
              <h2 id="pm-summary" className="flex-1 text-lg font-semibold">
                {t("pmBoard.scriptOverview")}
              </h2>
              <Button variant="accent" onPress={() => setShowOverview(true)}>
                <Eye aria-hidden="true" />
                {t("pmBoard.scriptOverviewOpen")}
              </Button>
            </section>
          ) : null}

          <section aria-label={t("pmBoard.readiness.label")} className="bg-background flex flex-wrap items-center gap-6 rounded-xl px-5 py-4 shadow-sm">
            <div className="flex min-w-48 flex-1 flex-col gap-2">
              <span className="text-muted-foreground text-xs">{t("pmBoard.readiness.tasks")}</span>
              <span className="text-2xl font-semibold tabular-nums">
                {done}/{tasks.length} <span className="text-muted-foreground text-sm font-normal">{t("pmBoard.readiness.done")}</span>
              </span>
              <span
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={tasks.length}
                aria-valuenow={done}
                aria-label={t("pmBoard.progressLabel", { done, total: tasks.length })}
                className="bg-border block h-1.5 overflow-hidden rounded-full"
              >
                <span className="bg-primary block h-full" style={{ width: `${tasks.length ? Math.round((done / tasks.length) * 100) : 0}%` }} />
              </span>
            </div>
            <div className="flex min-w-72 flex-[2] flex-col gap-2">
              <span className="text-muted-foreground text-xs">{t("pmBoard.readiness.openByColumn")}</span>
              <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
                {openByColumn.map((c) => (
                  <li key={c.id} className="bg-muted rounded-full px-2.5 py-0.5 text-xs tabular-nums">
                    {t("pmBoard.readiness.column", { title: c.title, count: c.count })}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {data.variants.length > 0 ? (
            <section aria-labelledby="pm-variants" className="bg-background flex flex-col gap-3 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <Layers aria-hidden="true" className="text-primary size-5" />
                <h2 id="pm-variants" className="text-lg font-semibold">
                  {t("pmBoard.variants")}
                </h2>
              </div>
              <ul className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(18rem,1fr))] gap-3 p-0">
                {data.variants.map((v) => (
                  <VariantCard
                    key={v.id}
                    session={session}
                    msId={ms.id}
                    meId={meId}
                    isAdmin={data.isAdmin}
                    variant={v}
                    annotations={data.variantAnnotations.filter((a) => a.variantId === v.id)}
                  />
                ))}
              </ul>
            </section>
          ) : null}

          <div className="mt-2 flex items-center gap-2">
            <h2 className="flex-1 text-lg font-semibold">{t("pmBoard.steps")}</h2>
            <span className="text-muted-foreground text-[0.8rem]">{t("pmBoard.stepCount", { count: data.steps.length })}</span>
          </div>
          {data.steps.map((step) => (
            <StepCard
              key={step.id}
              session={session}
              msId={ms.id}
              meId={meId}
              isAdmin={data.isAdmin}
              step={step}
              annotations={data.stepAnnotations.filter((a) => a.stepId === step.id)}
              openTasks={tasks.filter((x) => x.stepId === step.id && x.columnId !== doneCol?.id).length}
              onNewTask={() => setNewTaskStep(step.id)}
            />
          ))}
          <p className="text-muted-foreground text-xs">{t("pmBoard.readOnly")}</p>
        </>
      )}

      <Dialog isOpen={showOverview} size="xl" onOpenChange={setShowOverview} aria-labelledby="pm-summary-title">
        <div className="flex flex-col gap-1">
          <DialogTitle id="pm-summary-title">{t("pmBoard.scriptOverview")}</DialogTitle>
          <DialogDescription>
            {ms.code} – {ms.title}
          </DialogDescription>
        </div>
        <PmMarkdown>{data.overview}</PmMarkdown>
        <DialogFooter>
          <Button variant="default" onPress={() => setShowOverview(false)}>
            {t("pmBoard.scriptOverviewClose")}
          </Button>
        </DialogFooter>
      </Dialog>

      {newTaskStep !== undefined ? (
        <PmNewTaskDialog session={session} data={data} initialStepId={newTaskStep} onClose={() => setNewTaskStep(undefined)} />
      ) : null}
    </div>
  );
}

function DiffPanel({ diff }: { diff: NonNullable<ReturnType<typeof usePmDrehbuchDiff>["data"]> }) {
  const { t } = useTranslation();
  const tone = { added: "success", changed: "primary", removed: "neutral" } as const;
  return (
    <section id="pm-diff" aria-label={t("pmBoard.diff.show", { version: diff.from })} className="bg-background flex flex-col gap-3 rounded-xl p-5 shadow-sm">
      {diff.steps.length === 0 ? <p className="text-muted-foreground text-sm">{t("pmBoard.diff.none")}</p> : null}
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {diff.steps.map((s) => {
          const shown = s.after ?? s.before!;
          return (
            <li key={s.stableKey} className="flex flex-wrap items-baseline gap-2 text-sm">
              <StatusChip tone={tone[s.change]}>{t(`pmBoard.diff.${s.change}`)}</StatusChip>
              <span className={cn(s.change === "removed" && "line-through")}>{t("pmBoard.diff.stepLabel", { number: shown.number, title: shown.title })}</span>
              {s.change === "changed" && s.before && s.before.title !== shown.title ? <span className="text-muted-foreground text-xs">← {s.before.title}</span> : null}
            </li>
          );
        })}
      </ul>
      {diff.overviewChanged ? <p className="text-muted-foreground text-sm">{t("pmBoard.diff.overviewChanged")}</p> : null}
      {diff.variantsChanged ? <p className="text-muted-foreground text-sm">{t("pmBoard.diff.variantsChanged")}</p> : null}
    </section>
  );
}

function VariantCard({
  session,
  msId,
  meId,
  isAdmin,
  variant,
  annotations,
}: {
  session: AuthSession;
  msId: number;
  meId: number;
  isAdmin: boolean;
  variant: PmVariant;
  annotations: PmVariantAnnotation[];
}) {
  const { t } = useTranslation();
  const writes = useVariantAnnotationWrites(session, msId);
  return (
    <li className="bg-muted flex min-w-0 flex-col gap-2 rounded-lg px-3.5 py-3">
      <span className="text-sm font-semibold">{variant.title}</span>
      {variant.text ? <span className="text-muted-foreground text-[0.8rem]">{variant.text}</span> : null}
      <Notes
        label={t("pmBoard.note.variantLabel", { title: variant.title })}
        notes={annotations}
        meId={meId}
        isAdmin={isAdmin}
        isSaving={writes.create.isPending}
        saveFailed={writes.create.isError}
        onCreate={(text, onSaved) => writes.create.mutate({ variantId: variant.id, text }, { onSuccess: onSaved })}
        onUpdate={(id, text) => writes.update.mutateAsync({ id, text })}
        onDelete={(id) => writes.remove.mutateAsync({ id })}
      />
    </li>
  );
}

function StepCard({
  session,
  msId,
  meId,
  isAdmin,
  step,
  annotations,
  openTasks,
  onNewTask,
}: {
  session: AuthSession;
  msId: number;
  meId: number;
  isAdmin: boolean;
  step: PmStep;
  annotations: PmStepAnnotation[];
  openTasks: number;
  onNewTask: () => void;
}) {
  const { t } = useTranslation();
  const writes = useStepAnnotationWrites(session, msId);
  const headingId = `pm-step-${step.id}`;
  return (
    <article aria-labelledby={headingId} className="bg-background flex flex-col gap-3 rounded-xl p-5 shadow-sm">
      <div className="flex flex-wrap items-center gap-3">
        <span aria-hidden="true" className="bg-primary text-primary-foreground inline-flex size-8 items-center justify-center rounded-full font-bold tabular-nums">
          {step.number}
        </span>
        <h3 id={headingId} className="flex-1 text-base font-semibold">
          <span className="sr-only">{step.number}. </span>
          {step.title}
        </h3>
        {openTasks > 0 ? <span className="bg-secondary text-secondary-foreground rounded-full px-2 py-0.5 text-xs">{t("pmBoard.openTasks", { count: openTasks })}</span> : null}
        <Button variant="outline" size="sm" onPress={onNewTask} aria-label={t("pmBoard.newTaskOnStep", { number: step.number })}>
          <Plus aria-hidden="true" />
          {t("pmBoard.newTask")}
        </Button>
      </div>
      {step.points.length > 0 ? (
        <ul className="m-0 flex list-disc flex-col gap-1 pl-16 text-sm">
          {step.points.map((p, i) => (
            <li key={i}>{p}</li>
          ))}
        </ul>
      ) : null}
      <div className="sm:ml-11">
        <Notes
          label={t("pmBoard.note.label", { number: step.number })}
          notes={annotations}
          meId={meId}
          isAdmin={isAdmin}
          isSaving={writes.create.isPending}
          saveFailed={writes.create.isError}
          onCreate={(text, onSaved) => writes.create.mutate({ stepId: step.id, text }, { onSuccess: onSaved })}
          onUpdate={(id, text) => writes.update.mutateAsync({ id, text })}
          onDelete={(id) => writes.remove.mutateAsync({ id })}
        />
      </div>
    </article>
  );
}

/** A step's or a variant's notes with the box to add one, collapsed until asked for; the caller brings the writes. */
function Notes({
  label,
  notes,
  meId,
  isAdmin,
  isSaving,
  saveFailed,
  onCreate,
  onUpdate,
  onDelete,
}: {
  label: string;
  notes: PmNoteData[];
  meId: number;
  isAdmin: boolean;
  isSaving: boolean;
  saveFailed: boolean;
  onCreate: (text: string, onSaved: () => void) => void;
  onUpdate: (id: number, text: string) => Promise<unknown>;
  onDelete: (id: number) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const panelId = useId();
  const save = () => {
    const text = draft.trim();
    if (text) onCreate(text, () => setDraft(""));
  };
  return (
    <div className="border-border flex flex-col gap-3 border-t pt-3">
      <Button variant="ghost" size="sm" aria-expanded={open} aria-controls={panelId} onPress={() => setOpen((v) => !v)} className="w-fit">
        <ChevronRight aria-hidden="true" className={cn("transition-transform motion-reduce:transition-none", open && "rotate-90")} />
        {t("pmBoard.note.toggle")}
        <span className="bg-muted text-muted-foreground rounded-full px-1.5 text-xs tabular-nums">{notes.length}</span>
      </Button>
      {/* `hidden`, not unmounted: an unsaved draft survives collapsing. No `flex` while closed, it would beat the attribute. */}
      <div id={panelId} hidden={!open} className={cn(open && "flex flex-col gap-3")}>
        {notes.map((a) => (
          <PmNote
            key={a.id}
            note={a}
            canEdit={a.author.userId === meId}
            canDelete={a.author.userId === meId || isAdmin}
            onUpdate={(text) => onUpdate(a.id, text)}
            onDelete={() => onDelete(a.id)}
          />
        ))}
        <TextField value={draft} onChange={setDraft} className="flex flex-col gap-1.5">
          <Label className="text-muted-foreground text-xs">{label}</Label>
          <Textarea rows={draft ? 3 : 1} placeholder={t("pmBoard.note.placeholder")} maxLength={10_000} />
        </TextField>
        {saveFailed ? (
          <p role="alert" className="text-destructive text-sm">
            {t("pmBoard.note.saveFailed")}
          </p>
        ) : null}
        {draft.trim() ? (
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onPress={() => setDraft("")} isDisabled={isSaving}>
              {t("pmBoard.note.cancel")}
            </Button>
            <Button variant="default" onPress={save} isDisabled={isSaving}>
              {t("pmBoard.note.save")}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
